import axiosConfig from '../../../redux/Project/axiosConfig';
import {
  logClientAuditEvent,
  logClientAuditEventAsync,
} from '../../project/services/auditTrailApi';
import { startNewAuditSession } from '../../audit/auditSession';

const COGNITO_MODULE = 'COGNITO';
const ENTITY_TYPE = 'USER_SESSION';
const USER_LOOKUP_TIMEOUT_MS = 1000;

interface LoginLocationContext {
  timezone: string | null;
  locale: string | null;
  userTimeZone: string | null;
}

type SignInResult = {
  attributes?: { email?: string; sub?: string; given_name?: string; family_name?: string };
  username?: string;
  signInUserSession?: {
    accessToken?: { payload?: Record<string, unknown> };
    idToken?: { payload?: Record<string, unknown> };
  };
};

function getIdTokenPayload(signInResult: SignInResult): Record<string, unknown> | undefined {
  const payload = signInResult?.signInUserSession?.idToken?.payload;
  return payload && typeof payload === 'object' ? payload : undefined;
}

function asNonEmptyString(value: unknown): string {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function extractCognitoGroups(signInResult: SignInResult): string[] {
  const groups = signInResult?.signInUserSession?.accessToken?.payload?.['cognito:groups'];
  return Array.isArray(groups) ? groups.map(String) : [];
}

function getBrowserLocationContext(): Pick<LoginLocationContext, 'timezone' | 'locale'> {
  let timezone: string | null = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timezone = null;
  }

  const locale = typeof navigator !== 'undefined' ? navigator.language || null : null;
  return { timezone, locale };
}

function formatLoginLocationLabel(location: LoginLocationContext): string | null {
  if (location.timezone) return location.timezone;
  if (location.userTimeZone) return location.userTimeZone;
  return location.locale;
}

function buildLoginSummary(email: string, roleLabel: string, location: LoginLocationContext): string {
  let summary = `${email} signed in (${roleLabel})`;
  const locationLabel = formatLoginLocationLabel(location);
  if (locationLabel) {
    summary += ` from ${locationLabel}`;
  }
  return `${summary}.`;
}

async function fetchUserProfile(cognitoId: string): Promise<Record<string, unknown> | null> {
  if (!cognitoId) return null;

  try {
    const response = await Promise.race([
      axiosConfig.get(`/user/getUserByCognitoId/${cognitoId}`),
      new Promise<null>((resolve) => {
        window.setTimeout(() => resolve(null), USER_LOOKUP_TIMEOUT_MS);
      }),
    ]);

    if (!response || typeof response !== 'object' || !('data' in response)) {
      return null;
    }

    const userData = (response as { data?: { data?: Record<string, unknown> } }).data?.data;
    return userData && typeof userData === 'object' ? userData : null;
  } catch {
    return null;
  }
}

/** Log successful user login with email and role when available. */
export async function logUserLoginAudit(
  signInResult: SignInResult,
  emailOverride?: string
): Promise<void> {
  try {
    const tokenPayload = getIdTokenPayload(signInResult);
    const loginEmail =
      emailOverride?.trim() ||
      asNonEmptyString(signInResult?.attributes?.email) ||
      asNonEmptyString(tokenPayload?.email) ||
      asNonEmptyString(signInResult?.username) ||
      '';

    if (!loginEmail) return;

    const cognitoId =
      asNonEmptyString(signInResult?.attributes?.sub) ||
      asNonEmptyString(tokenPayload?.sub) ||
      '';

    const cognitoGroups = extractCognitoGroups(signInResult);
    const browserLocation = getBrowserLocationContext();

    let roleName: string | null = cognitoGroups[0] ?? null;
    let userId: number | null = null;
    let displayName: string | null = null;
    let userTimeZone: string | null = null;

    const userData = await fetchUserProfile(cognitoId);
    if (userData) {
      userId = typeof userData.user_id === 'number' ? userData.user_id : null;
      const appLevelRole = userData.appLevelRole as { group_name?: string } | undefined;
      roleName = appLevelRole?.group_name ?? roleName;
      userTimeZone = typeof userData.time_zone === 'string' ? userData.time_zone : null;
      const first =
        asNonEmptyString(userData.first_name) ||
        asNonEmptyString(signInResult?.attributes?.given_name);
      const last =
        asNonEmptyString(userData.last_name) ||
        asNonEmptyString(signInResult?.attributes?.family_name);
      displayName = `${first} ${last}`.trim() || null;
    }

    const location: LoginLocationContext = {
      ...browserLocation,
      userTimeZone,
    };

    const roleLabel = roleName ?? 'unknown';

    startNewAuditSession();

    logClientAuditEvent({
      eventType: 'AUTH_LOGIN_SUCCESS',
      module: COGNITO_MODULE,
      entityType: ENTITY_TYPE,
      entityId: cognitoId || loginEmail,
      entityName: displayName ?? loginEmail,
      entityEmail: loginEmail,
      severity: 'INFO',
      summary: buildLoginSummary(loginEmail, roleLabel, location),
      displayTitle: loginEmail,
      actionDetails: {
        email: loginEmail,
        role: roleName,
        roleName,
        cognitoGroups,
        userId,
        location,
      },
    });
  } catch (error) {
    console.warn('Failed to log user login audit', error);
  }
}

/** Log failed login attempt (no authenticated session). */
export function logUserLoginFailedAudit(emailAttempted: string, reason: string): void {
  const email = emailAttempted?.trim();
  if (!email) return;

  logClientAuditEvent({
    eventType: 'AUTH_LOGIN_FAILED',
    module: COGNITO_MODULE,
    entityType: ENTITY_TYPE,
    entityId: email,
    entityEmail: email,
    severity: 'ERR',
    summary: `Failed login attempt for ${email}.`,
    displayTitle: email,
    actionDetails: {
      emailAttempted: email,
      reason: reason || 'Unknown error',
      location: getBrowserLocationContext(),
    },
  });
}

/**
 * Log user logout while the auth token is still valid.
 * Await this before clearTokens() so createAuditTrailRecord is authenticated.
 */
export async function logUserLogoutAudit(): Promise<void> {
  try {
    const raw = localStorage.getItem('user');
    const user = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const email =
      (typeof user.email === 'string' && user.email) ||
      (typeof user['custom:email'] === 'string' && user['custom:email']) ||
      '';
    const cognitoId =
      (typeof user.sub === 'string' && user.sub) ||
      (typeof user['cognito:username'] === 'string' && user['cognito:username']) ||
      email ||
      'unknown';

    const firstName =
      (typeof user['custom:first_name'] === 'string' && user['custom:first_name']) ||
      (typeof user.given_name === 'string' && user.given_name) ||
      '';
    const lastName =
      (typeof user['custom:last_name'] === 'string' && user['custom:last_name']) ||
      (typeof user.family_name === 'string' && user.family_name) ||
      '';
    const displayName = `${firstName} ${lastName}`.trim() || email || 'User';

    await logClientAuditEventAsync({
      eventType: 'AUTH_LOGOUT',
      module: COGNITO_MODULE,
      entityType: ENTITY_TYPE,
      entityId: String(cognitoId),
      entityName: displayName,
      entityEmail: email || undefined,
      severity: 'INFO',
      summary: email ? `${email} signed out.` : `${displayName} signed out.`,
      displayTitle: email || displayName,
      actionDetails: {
        email: email || null,
        cognitoId,
      },
    });
  } catch (error) {
    console.warn('Failed to log user logout audit', error);
  }
}
